import React from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import DeploymentSettingsPage from "./DeploymentSettingsPage";
import TeamDetailPage from "./TeamDetailPage";
import ProjectConfigurationPage from "./ProjectConfigurationPage";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { selectPermissionEntry, usePermissionStore } from "@presentation/stores/PermissionStore";
import { useProjectPermission, useTeamPermission } from "@presentation/components/helpers/usePermissionCheck";
import { settingsUrl, type SettingsScope } from "@presentation/common/settingsRoutes";
import { pageShellStyle } from "@presentation/common/pageLayout";
import { ProjectActions, TeamActions } from "@domain/schemas/workspace";
import { useWorkspaceFilters } from "@presentation/hooks/useWorkspaceFilters";

const ScopedSettings: React.FC<{ scope: Exclude<SettingsScope, { level: "deployment" }> }> = ({ scope }) => {
  const teamId = scope.level === "team" ? scope.teamId : null;
  const projectId = scope.level === "project" ? scope.projectId : null;
  const teamPermission = useTeamPermission(teamId, TeamActions.ViewInventory);
  const projectPermission = useProjectPermission(projectId, ProjectActions.View);
  const entry = usePermissionStore(selectPermissionEntry({ teamId: teamId ?? undefined, projectId: projectId ?? undefined }));
  const allowed = scope.level === "team" ? teamPermission.allowed : projectPermission.allowed;

  if (!allowed) {
    if (entry.status === "error") return <p role="alert">Could not verify access to these settings. Reload the page to try again.</p>;
    if (entry.status === "loaded")
      return (
        <p role="alert">
          You do not have access to these {scope.level} settings. <Link to="/projects">Choose another project</Link>.
        </p>
      );
    return <p role="status">Loading settings permissions...</p>;
  }

  return scope.level === "team" ? <TeamDetailPage teamId={scope.teamId} /> : <ProjectConfigurationPage projectId={scope.projectId} />;
};

const SettingsPage: React.FC = () => {
  const filters = useWorkspaceFilters();
  const [params] = useSearchParams();
  const workspace = useWorkspaceContextStore();
  const isAdmin = usePermissionStore((state) => state.isAdmin);
  const level = params.get("scope");
  const projectId = params.get("projectId") ?? "";
  const teamId = params.get("teamId") ?? "";

  if (!level) {
    if (projectId) return <Navigate to={settingsUrl({ level: "project", projectId }, params)} replace />;
    if (teamId) return <Navigate to={settingsUrl({ level: "team", teamId }, params)} replace />;
    if (workspace.status === "idle" || workspace.status === "loading") return <p role="status">Loading settings context...</p>;
    if (workspace.status === "error") return <p role="alert">Could not load settings context. Reload the page to try again.</p>;
    if (filters.scope === "project" && filters.projectId) return <Navigate to={settingsUrl({ level: "project", projectId: filters.projectId }, params)} replace />;
    if (filters.scope === "team" && filters.teamId) return <Navigate to={settingsUrl({ level: "team", teamId: filters.teamId }, params)} replace />;
    if (isAdmin) return <Navigate to={settingsUrl({ level: "deployment" }, params)} replace />;
    return (
      <p>
        No team or project settings are available. <Link to="/teams">View teams</Link>.
      </p>
    );
  }

  if (!["deployment", "team", "project"].includes(level) || (level === "team" && !teamId) || (level === "project" && !projectId)) {
    return (
      <p role="alert">
        Invalid settings scope. <Link to="/settings">Open settings for your current context</Link>.
      </p>
    );
  }

  return (
    <div style={pageShellStyle}>
      <div style={{ flex: "1 1 auto", minHeight: 0 }}>
        {level === "deployment" && <DeploymentSettingsPage />}
        {level === "team" && <ScopedSettings key={`team:${teamId}`} scope={{ level: "team", teamId }} />}
        {level === "project" && <ScopedSettings key={`project:${projectId}`} scope={{ level: "project", projectId }} />}
      </div>
    </div>
  );
};

export default SettingsPage;
